/**
 * Bulletproof Context Menu — operational interaction infrastructure (Phase 178.5.F).
 *
 * Built on top of `bulletproof-menu.jsx`.  Re-uses the same `<DropdownItem>`
 * reliability primitives but adds the context-menu trigger surface:
 *
 *   • Desktop right-click (`contextmenu` event)
 *   • Mobile long-press (configurable threshold, default 350ms)
 *   • Keyboard (Shift+F10, ContextMenu key, Enter on focused row)
 *
 * RENDERING DOCTRINE
 * ──────────────────
 *  • Menu renders via `createPortal` into `document.body` — NEVER clipped
 *    by parent `overflow:hidden` containers (essential for nested table
 *    rows, scroll containers, slot calendars, etc.).
 *  • Position is calculated at trigger time from the pointer event
 *    (`clientX`, `clientY`) and clamped to viewport with collision
 *    detection (flips left if right edge would overflow, flips up if
 *    bottom edge would overflow).
 *  • Anchor stays anchored across scroll/resize — menu closes on either
 *    so we never show a stale position.
 *
 * INTENT COLOR HIERARCHY
 * ──────────────────────
 *  • neutral (default) — slate
 *  • success           — emerald
 *  • warning           — amber
 *  • danger            — rose
 *  Each variant tints both the icon and (subtly) the hover background.
 *
 * USAGE — items[] API (recommended for most cases)
 * ────────────────────────────────────────────────
 *
 *   <BulletproofContextMenu
 *     items={[
 *       { label: 'Accept booking', icon: Check, variant: 'success', action: handleAccept },
 *       { label: 'Refund', icon: RotateCcw, variant: 'danger', action: handleRefund },
 *       { separator: true },
 *       { label: 'View profile', href: `/students/${id}`, variant: 'neutral' },
 *     ]}
 *     onMenuOpen={({ entity_type, row_id }) => track('context_menu_opened', { entity_type, row_id })}
 *     onActionSelected={(item, meta) => track('context_action_selected', { ...meta, label: item.label })}
 *     onCancelled={(meta) => track('context_menu_cancelled', meta)}
 *     metadata={{ entity_type: 'booking', row_id: booking.id }}
 *     longPressMs={350}
 *   >
 *     <tr>...</tr>
 *   </BulletproofContextMenu>
 *
 * USAGE — compound API (advanced cases needing customRender)
 * ──────────────────────────────────────────────────────────
 *
 *   <BulletproofContextMenu>
 *     <BulletproofContextMenuTrigger asChild>
 *       <tr>...</tr>
 *     </BulletproofContextMenuTrigger>
 *     <BulletproofContextMenuContent>
 *       <ContextMenuLabel>Booking actions</ContextMenuLabel>
 *       <ContextMenuItem icon={Check} variant="success" onSelect={accept}>Accept</ContextMenuItem>
 *       <ContextMenuDivider />
 *       <ContextMenuDangerItem icon={X} onSelect={reject}>Reject</ContextMenuDangerItem>
 *     </BulletproofContextMenuContent>
 *   </BulletproofContextMenu>
 */
import {
  Children,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const ContextMenuContext = createContext(null);

const VARIANT_STYLES = {
  neutral: {
    iconColor: 'hsl(var(--ax-text-muted))',
    textColor: 'hsl(var(--ax-text))',
    hoverBg: 'rgba(0,0,0,0.05)',
  },
  success: {
    iconColor: '#10b981',
    textColor: 'hsl(var(--ax-text))',
    hoverBg: 'rgba(16,185,129,0.08)',
  },
  warning: {
    iconColor: '#f59e0b',
    textColor: 'hsl(var(--ax-text))',
    hoverBg: 'rgba(245,158,11,0.10)',
  },
  danger: {
    iconColor: 'hsl(0 70% 55%)',
    textColor: 'hsl(0 70% 55%)',
    hoverBg: 'rgba(244,63,94,0.10)',
  },
};

const MENU_WIDTH = 220;
const MENU_PADDING = 8;
const DEFAULT_LONG_PRESS_MS = 350;

export function useBulletproofContextMenu() {
  const ctx = useContext(ContextMenuContext);
  if (!ctx) {
    throw new Error('useBulletproofContextMenu() must be used inside <BulletproofContextMenu>.');
  }
  return ctx;
}

/**
 * Clamps menu position to viewport with collision-aware flipping.
 * Returns { x, y, anchor } where `anchor` indicates which corner of the
 * menu is positioned at (x,y) — useful for future entrance animations.
 */
function clampMenuPosition(clientX, clientY, menuWidth, menuHeight) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let x = clientX;
  let y = clientY;
  let anchor = 'top-left';

  // Right-edge overflow → flip left
  if (x + menuWidth + MENU_PADDING > vw) {
    x = Math.max(MENU_PADDING, x - menuWidth);
    anchor = anchor.replace('left', 'right');
  }
  // Bottom-edge overflow → flip up
  if (y + menuHeight + MENU_PADDING > vh) {
    y = Math.max(MENU_PADDING, y - menuHeight);
    anchor = anchor.replace('top', 'bottom');
  }
  // Final clamp (in case menu is taller than viewport)
  x = Math.max(MENU_PADDING, Math.min(x, vw - menuWidth - MENU_PADDING));
  y = Math.max(MENU_PADDING, Math.min(y, vh - menuHeight - MENU_PADDING));
  return { x, y, anchor };
}

/**
 * Root.  When given `children` as a single element + `items[]`, auto-wires
 * the trigger + content (the 80% case).  When given multiple children
 * (Trigger + Content), behaves as a compound primitive (escape hatch).
 */
export function BulletproofContextMenu({
  children,
  items,
  metadata = {},
  longPressMs = DEFAULT_LONG_PRESS_MS,
  onMenuOpen,
  onActionSelected,
  onCancelled,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0, anchor: 'top-left' });
  const longPressTimer = useRef(null);
  const longPressStartPos = useRef(null);
  const contentRef = useRef(null);
  const triggerRef = useRef(null);
  // Track whether the menu closed because an action committed (selected)
  // or because it was cancelled (outside-click / Esc) so analytics fire
  // exactly once per session, with the right verb.
  const actionCommittedRef = useRef(false);

  const close = useCallback((reason = 'cancelled') => {
    setOpen(false);
    if (reason === 'cancelled' && onCancelled) {
      try { onCancelled(metadata); } catch (_) { /* non-fatal */ }
    }
  }, [onCancelled, metadata]);

  const openAt = useCallback((clientX, clientY) => {
    if (disabled) return;
    // Estimate height conservatively (items length × row height + paddings).
    // True height resolves on next paint; we re-clamp via useLayoutEffect.
    const estimatedHeight = Math.min(
      window.innerHeight - 2 * MENU_PADDING,
      40 + (items ? items.length : 6) * 36
    );
    const clamped = clampMenuPosition(clientX, clientY, MENU_WIDTH, estimatedHeight);
    setPosition(clamped);
    actionCommittedRef.current = false;
    setOpen(true);
    if (onMenuOpen) {
      try { onMenuOpen({ ...metadata, x: clientX, y: clientY }); } catch (_) { /* non-fatal */ }
    }
  }, [disabled, items, onMenuOpen, metadata]);

  const commitAction = useCallback((item) => {
    actionCommittedRef.current = true;
    if (onActionSelected) {
      try { onActionSelected(item, metadata); } catch (_) { /* non-fatal */ }
    }
    setOpen(false);
  }, [onActionSelected, metadata]);

  // ── Outside-click & Esc handlers ──────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (contentRef.current && !contentRef.current.contains(e.target)) {
        close('cancelled');
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close('cancelled');
        triggerRef.current?.focus?.();
      }
    };
    const handleScroll = () => close('cancelled');
    document.addEventListener('pointerdown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('pointerdown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, close]);

  // ── Re-clamp once true menu height is known (next paint) ─────────
  useEffect(() => {
    if (!open || !contentRef.current) return;
    const rect = contentRef.current.getBoundingClientRect();
    const reclamped = clampMenuPosition(position.x, position.y, rect.width, rect.height);
    if (reclamped.x !== position.x || reclamped.y !== position.y) {
      setPosition(reclamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Trigger handlers (right-click, long-press, keyboard) ─────────
  const triggerHandlers = useMemo(() => ({
    onContextMenu: (e) => {
      if (disabled) return;
      e.preventDefault();
      openAt(e.clientX, e.clientY);
    },
    onTouchStart: (e) => {
      if (disabled) return;
      const t = e.touches[0];
      longPressStartPos.current = { x: t.clientX, y: t.clientY };
      longPressTimer.current = setTimeout(() => {
        if (longPressStartPos.current) {
          openAt(longPressStartPos.current.x, longPressStartPos.current.y);
        }
      }, longPressMs);
    },
    onTouchMove: (e) => {
      if (!longPressStartPos.current || !longPressTimer.current) return;
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - longPressStartPos.current.x);
      const dy = Math.abs(t.clientY - longPressStartPos.current.y);
      // 10px tolerance — finger jitter doesn't cancel; real scroll does.
      if (dx > 10 || dy > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        longPressStartPos.current = null;
      }
    },
    onTouchEnd: () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      longPressStartPos.current = null;
    },
    onKeyDown: (e) => {
      if (disabled) return;
      // Shift+F10 and ContextMenu key are the standard a11y triggers.
      if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
        e.preventDefault();
        const rect = (triggerRef.current || e.currentTarget).getBoundingClientRect();
        openAt(rect.left + 8, rect.top + rect.height / 2);
      }
    },
  }), [disabled, openAt, longPressMs]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  // ── Auto-detect compound vs items[] API ──────────────────────────
  const childArray = Children.toArray(children).filter(Boolean);
  const hasItemsApi = Array.isArray(items) && items.length > 0;
  const isCompound = childArray.some((c) =>
    c?.type === BulletproofContextMenuTrigger || c?.type === BulletproofContextMenuContent
  );

  // If using compound API, render children directly and let Trigger/Content
  // hook into context for state.
  const contextValue = {
    open, position, close, openAt, commitAction,
    triggerHandlers, contentRef, triggerRef, disabled, metadata,
  };

  if (isCompound) {
    return (
      <ContextMenuContext.Provider value={contextValue}>
        {children}
      </ContextMenuContext.Provider>
    );
  }

  // Items[] API — auto-wire the trigger and render the items.
  const onlyChild = childArray[0];
  if (!onlyChild) return null;

  const trigger = cloneElement(onlyChild, {
    ref: (node) => { triggerRef.current = node; },
    ...triggerHandlers,
    tabIndex: onlyChild.props?.tabIndex ?? 0,
    style: { ...(onlyChild.props?.style || {}), WebkitTouchCallout: 'none' },
  });

  return (
    <ContextMenuContext.Provider value={contextValue}>
      {trigger}
      {open && hasItemsApi
        ? createPortal(
            <ContextMenuPortalContent
              items={items}
              position={position}
              contentRef={contentRef}
              commitAction={commitAction}
            />,
            document.body
          )
        : null}
    </ContextMenuContext.Provider>
  );
}

/**
 * Internal portal renderer for the items[] API.
 */
function ContextMenuPortalContent({ items, position, contentRef, commitAction }) {
  return (
    <div
      ref={contentRef}
      role="menu"
      data-testid="bulletproof-context-menu"
      className="fixed rounded-xl border shadow-2xl py-1 overflow-hidden"
      style={{
        left: position.x,
        top: position.y,
        minWidth: MENU_WIDTH,
        maxWidth: 320,
        background: 'var(--ax-card-bg-soft, white)',
        borderColor: 'var(--ax-border-bold, rgba(0,0,0,0.1))',
        backdropFilter: 'blur(12px)',
        zIndex: 9999,
      }}
    >
      {items.map((item, idx) => {
        if (item.separator) {
          return <ContextMenuDivider key={`sep-${idx}`} testid={`context-divider-${idx}`} />;
        }
        if (item.label && !item.action && !item.href && item.header) {
          return (
            <ContextMenuLabel key={`label-${idx}`} testid={`context-label-${idx}`}>
              {item.label}
            </ContextMenuLabel>
          );
        }
        const Comp = item.variant === 'danger' ? ContextMenuDangerItem : ContextMenuItem;
        return (
          <Comp
            key={item.id || item.label || idx}
            icon={item.icon}
            variant={item.variant}
            disabled={item.disabled}
            href={item.href}
            external={item.external}
            testid={item.testid || `context-action-${idx}`}
            onSelect={() => {
              commitAction(item);
              if (item.action) {
                try { item.action(); } catch (_) { /* non-fatal */ }
              }
            }}
          >
            {item.label}
          </Comp>
        );
      })}
    </div>
  );
}

/**
 * Trigger for the compound API.  `asChild` clones the only child and binds
 * the context-menu handlers to it.
 */
export function BulletproofContextMenuTrigger({ children, asChild = true, ...props }) {
  const { triggerHandlers, triggerRef } = useBulletproofContextMenu();
  if (!asChild) {
    return (
      <div ref={triggerRef} {...triggerHandlers} {...props}>
        {children}
      </div>
    );
  }
  const only = Children.only(children);
  return cloneElement(only, {
    ref: (node) => { triggerRef.current = node; },
    ...triggerHandlers,
    tabIndex: only.props?.tabIndex ?? 0,
    style: { ...(only.props?.style || {}), WebkitTouchCallout: 'none' },
  });
}

/**
 * Content panel for the compound API.  Always portals into document.body.
 */
export function BulletproofContextMenuContent({ children, className, ...props }) {
  const { open, position, contentRef } = useBulletproofContextMenu();
  if (!open) return null;
  return createPortal(
    <div
      ref={contentRef}
      role="menu"
      data-testid={props['data-testid'] || 'bulletproof-context-menu'}
      className={className || 'fixed rounded-xl border shadow-2xl py-1 overflow-hidden'}
      style={{
        left: position.x,
        top: position.y,
        minWidth: MENU_WIDTH,
        maxWidth: 320,
        background: 'var(--ax-card-bg-soft, white)',
        borderColor: 'var(--ax-border-bold, rgba(0,0,0,0.1))',
        backdropFilter: 'blur(12px)',
        zIndex: 9999,
        ...(props.style || {}),
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/**
 * Generic menu item.  Variant tints the icon and the hover background.
 */
export function ContextMenuItem({
  children,
  icon: Icon,
  variant = 'neutral',
  disabled = false,
  href,
  external = false,
  onSelect,
  testid,
}) {
  const { commitAction, close } = useBulletproofContextMenu() || {};
  const navigate = useNavigate();
  const styleSet = VARIANT_STYLES[variant] || VARIANT_STYLES.neutral;
  const [hover, setHover] = useState(false);

  const commit = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (disabled) return;
    if (commitAction) commitAction({ label: typeof children === 'string' ? children : null, variant, href });
    if (onSelect) {
      try { onSelect(); } catch (_) { /* non-fatal */ }
    } else if (href) {
      if (external) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    }
    if (close) close('selected');
  }, [disabled, commitAction, onSelect, href, external, navigate, children, variant, close]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') commit(e);
  };

  const sharedProps = {
    onMouseDown: commit,
    onKeyDown: handleKeyDown,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    role: 'menuitem',
    tabIndex: disabled ? -1 : 0,
    'data-testid': testid,
    'aria-disabled': disabled || undefined,
    style: {
      width: '100%',
      padding: '8px 14px',
      fontSize: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      color: styleSet.textColor,
      background: hover && !disabled ? styleSet.hoverBg : 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background-color 120ms ease',
      textAlign: 'left',
      border: 'none',
      outline: 'none',
    },
  };

  const inner = (
    <>
      {Icon ? (
        <Icon
          className="flex-shrink-0"
          style={{ width: 14, height: 14, color: styleSet.iconColor }}
        />
      ) : null}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={(e) => { if (e.button === 0) e.preventDefault(); }}
        {...sharedProps}
      >
        {inner}
      </a>
    );
  }
  return (
    <button type="button" disabled={disabled} {...sharedProps}>
      {inner}
    </button>
  );
}

/**
 * Pre-styled danger variant — same as `<ContextMenuItem variant="danger">`
 * but shorter to type for the common case.
 */
export function ContextMenuDangerItem(props) {
  return <ContextMenuItem {...props} variant="danger" />;
}

/**
 * Non-interactive section header.
 */
export function ContextMenuLabel({ children, testid }) {
  return (
    <div
      style={{
        padding: '8px 14px 4px',
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: '0.20em',
        fontWeight: 700,
        color: 'hsl(var(--ax-text-faint))',
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/**
 * Horizontal separator between sections.
 */
export function ContextMenuDivider({ testid }) {
  return (
    <div
      role="separator"
      style={{
        height: 1,
        margin: '4px 0',
        background: 'var(--ax-divider, rgba(0,0,0,0.08))',
      }}
      data-testid={testid}
    />
  );
}
